import {
    createAsyncThunk,
    createSelector,
    createSlice,
    type PayloadAction,
    type SerializedError,
} from "@reduxjs/toolkit";
import {
    AUDIO_CHANNEL_IDS,
    type AudioLiveStreamChannels,
} from "@foundation/live-schedule";

import BloombergRadioImage from "assets/bloomberg-radio.png";
import {
    fetchLiveRadioScheduleFromApi,
    type LiveRadioEpisode,
} from "~api/client/fetchLiveRadioScheduleFromApi";
import { type EpisodeMetadata } from "~components/modules/lineup-content/variants/audio-player/RadioEpisodeMetadata";
import { type RootState } from "~redux/store";

const DEFAULT_EPISODE_METADATA = {
    description: "",
    title: "Bloomberg Radio",
    thumbnail: BloombergRadioImage.src,
} as const satisfies EpisodeMetadata;

export const RADIO_STATION_TO_SCHEDULE_ID = {
    Boston: AUDIO_CHANNEL_IDS.BOSTON_RADIO,
    National: AUDIO_CHANNEL_IDS.WBBR_RADIO,
    "New York": AUDIO_CHANNEL_IDS.WBBR_RADIO,
    "Washington D.C.": AUDIO_CHANNEL_IDS.WASHINGTON_DC_RADIO,
} as const;

export type RadioStation = keyof typeof RADIO_STATION_TO_SCHEDULE_ID;

// Business logic (constants and types)
export type TritonStreamID =
    | "RADIO_BOS"
    | "RADIO_DC"
    | "RADIO_NATIONAL"
    | "RADIO_NY";

export const PATH_TO_RADIO_STATION: Record<string, RadioStation> = {
    boston: "Boston",
    newyork: "New York",
    dc: "Washington D.C.",
    national: "National",
} as const;

export const RADIO_STATION_TO_STREAM_ID: Record<RadioStation, TritonStreamID> =
    {
        Boston: "RADIO_BOS",
        National: "RADIO_NATIONAL",
        "New York": "RADIO_NY",
        "Washington D.C.": "RADIO_DC",
    } as const;

type RadioScheduleId =
    (typeof RADIO_STATION_TO_SCHEDULE_ID)[keyof typeof RADIO_STATION_TO_SCHEDULE_ID];

export interface LiveRadioState {
    schedule: {
        id: RadioScheduleId;
        loading: boolean;
        data: LiveRadioEpisode[];
        error: SerializedError | null;
    };
    station: RadioStation;

    /**
     * `currentEpisodeIndex === -1`: empty schedule or all episodes are in the future
     * `currentEpisodeIndex === schedule.length`: all episodes in the schedule has ended
     */
    currentEpisodeIndex: number;
}

export const initialState: LiveRadioState = {
    schedule: {
        id: AUDIO_CHANNEL_IDS.WBBR_RADIO,
        loading: false,
        data: [],
        error: null,
    },
    station: "National",
    currentEpisodeIndex: -1,
};

export const fetchLiveRadioSchedule = createAsyncThunk<
    LiveRadioEpisode[],
    AudioLiveStreamChannels,
    { state: RootState }
>("liveRadio/schedule", (station, thunkAPI) =>
    fetchLiveRadioScheduleFromApi(station, thunkAPI.signal)
);

/**
 * TODO: Use `Array.prototype.findLastIndex` once the tsconfig option `lib`
 * in `tsconfig.prod.json` is updated to `es2023` or later.
 */
const findCurrentEpisodeIndex = (episodes: LiveRadioEpisode[]) => {
    const now = new Date();

    for (let i = episodes.length - 1; i >= 0; i--) {
        if (new Date(episodes[i].episodeInfo.episodeStartTime) <= now) {
            return i;
        }
    }

    return -1;
};

const liveRadioSlice = createSlice({
    name: "liveRadio",
    initialState,
    reducers: {
        changeRadioStation: (
            state,
            action: PayloadAction<(typeof initialState)["station"]>
        ) => {
            state.schedule.id = RADIO_STATION_TO_SCHEDULE_ID[action.payload];
            state.station = action.payload;
        },
        setCurrentEpisodeIndex: (state, action: PayloadAction<number>) => {
            state.currentEpisodeIndex = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLiveRadioSchedule.pending, (state) => {
                state.schedule.loading = true;
                state.schedule.error = null;
            })
            .addCase(fetchLiveRadioSchedule.fulfilled, (state, action) => {
                const episodes = action.payload;
                state.schedule.loading = false;
                state.schedule.data = episodes;
                state.currentEpisodeIndex = findCurrentEpisodeIndex(episodes);
            })
            .addCase(fetchLiveRadioSchedule.rejected, (state, action) => {
                state.schedule.loading = false;
                state.schedule.error = action.error;
            });
    },
});

export const { changeRadioStation, setCurrentEpisodeIndex } =
    liveRadioSlice.actions;
export default liveRadioSlice.reducer;

export const selectSchedule = (state: RootState) => state.liveRadio.schedule;
export const selectStation = (state: RootState) => state.liveRadio.station;
export const selectCurrentEpisodeIndex = (state: RootState) =>
    state.liveRadio.currentEpisodeIndex;
export const selectEpisodeMetadata = createSelector(
    [selectSchedule, selectCurrentEpisodeIndex],
    ({ data: episodes }, currentEpisodeIndex) =>
        currentEpisodeIndex >= 0 && currentEpisodeIndex < episodes.length
            ? {
                  description:
                      episodes[currentEpisodeIndex].episodeInfo
                          .episodeDescription,
                  title:
                      episodes[currentEpisodeIndex].episodeInfo.episodeTitle ||
                      "Bloomberg Radio",
                  thumbnail:
                      episodes[currentEpisodeIndex].episodeInfo
                          .squareImageThumbnail || BloombergRadioImage.src,
              }
            : DEFAULT_EPISODE_METADATA
);
